import type { EcwidSettings } from "@/app/services/generated/ecwidsettingsService";
import { computed, onMounted } from "vue";
import { EnumIntegrationStatus } from "@/components/pages/integrations/types";
import type { IntegrationMeta } from "@/components/pages/integrations/composables/types";
import { integrationsMenu } from "@/app/ui/navigation/integrationsMenu";
import img from "@/assets/images/app/integrations/ecwid.svg";
import { ecwidSettingsService } from "@/app/services/generated/ecwidsettingsService";
import { BrewManError } from "@/app/errors";
import messages from "@/plugins/i18n";
import router from "@/router/router";
import { showConfirmationDialog } from "@/components/composables/promptDialogs";
import {
  makeDeepRef,
  makeRef,
  withLoading,
} from "@/components/composables/componentTools";
import { ecwidLinkService } from "@/app/services/generated/ecwidlinkService";
import type { EcwidOrder } from "@/app/services/generated/ecwidorderService";
import {
  EcwidOrderFilter,
  ecwidOrderService,
} from "@/app/services/generated/ecwidorderService";
import type { LocalDate } from "@js-joda/core";
import { lastSixtyDays } from "@/components/controls/daterange/dateRangePresets";
import type { Order } from "@/app/services/generated/orderService";
import { orderService } from "@/app/services/generated/orderService";
import { distinctGuids, toGuidMap } from "@/app/apiutils";
import { GuidMap } from "@/app/types/common/guidcollections";
import { fillFilterDates } from "@/store/modules/integrationErrorStore";
import type { Maybe } from "@/components/utils/typeHelpers";

export function ecwidMeta(
  settings: Maybe<EcwidSettings>,
  isAuthenticated: boolean
): IntegrationMeta {
  const status = settings?.outletId
    ? isAuthenticated
      ? EnumIntegrationStatus.active
      : EnumIntegrationStatus.unauthenticated
    : EnumIntegrationStatus.inactive;

  return {
    name: messages.ecwid_title,
    description: messages.ecwid_description,
    integrationRoute: integrationsMenu.ecwid.viewLocation(),
    miniImg: img,
    img,
    url: messages.ecwid_url,
    activated: status.isActive(),
    status,
  };
}

const callbackUrl = window.location.origin.toString() + "/integrations/ecwid";

const ecwidAuthExpected = "expecting-ecwid";

export function useEcwidIntegration() {
  const settings = makeDeepRef<EcwidSettings | null>(null);
  const isActivated = computed(() => integrationStatus.value.isActive());
  const isAuthenticated = makeRef(false);

  const ecwidOrders = makeRef<readonly EcwidOrder[]>([]);
  const brewmanOrderMap = makeRef(new GuidMap<Order>());
  const dateRange = makeRef(lastSixtyDays.asDateRange());

  const integrationStatus = computed(() => {
    if (!settings.value || !settings.value.outletId) {
      return EnumIntegrationStatus.inactive;
    }

    return isAuthenticated.value
      ? EnumIntegrationStatus.active
      : EnumIntegrationStatus.unauthenticated;
  });

  const meta = computed(() => ecwidMeta(settings.value, isAuthenticated.value));

  const refresh = withLoading(async () => {
    settings.value = await ecwidSettingsService.getOrCreateEcwidSettings();

    [isAuthenticated.value] = await Promise.all([
      ecwidSettingsService.isEcwidAuthenticated(settings.value.id),
      refreshOrders(),
    ]);
  });

  const saveSettings = withLoading(async (newSettings: EcwidSettings) => {
    await ecwidSettingsService.saveEcwidSettings(newSettings);
  });

  const disconnect = async () => {
    if (!settings.value) {
      return;
    }

    const confirmed = await showConfirmationDialog(
      messages.prompt_confirm_title,
      messages.ecwid_disconnect
    );

    if (!confirmed) {
      return;
    }

    await ecwidSettingsService.hardDeleteEcwidSettings(settings.value.id);

    await router.push(integrationsMenu.rootViewLocation());
  };

  const beginAuth = withLoading(async (newSettings: EcwidSettings) => {
    await saveSettings(newSettings);

    const ecwidLoginUrl = await ecwidSettingsService.createEcwidLoginUrl(
      newSettings.id,
      callbackUrl
    );

    localStorage.setItem(ecwidAuthExpected, "true");

    window.open(ecwidLoginUrl, "_self");
  });

  const completeAuth = withLoading(async () => {
    if (!settings.value) {
      throw new BrewManError(
        messages.squarespace_cannot_complete_auth_with_no_settings
      );
    }

    if (!localStorage.getItem(ecwidAuthExpected)) {
      localStorage.removeItem(ecwidAuthExpected);
      throw new BrewManError(messages.unexpected_authentication_process);
    }

    const queryString = router.currentRoute.fullPath.split("?")?.[1];

    if (!queryString) {
      throw new BrewManError("No valid query string given");
    }

    await ecwidSettingsService.completeEcwidAuthentication(
      settings.value.id,
      queryString,
      callbackUrl
    );

    localStorage.removeItem(ecwidAuthExpected);
    await router.replace(integrationsMenu.ecwid.viewLocation()); // Remove query params
    await refresh();
  });

  const initiateOrderImport = withLoading(async () => {
    if (!settings.value) {
      return;
    }

    await ecwidLinkService.initiateOrderImport(settings.value.id);
  });

  const refreshOrders = withLoading(async () => {
    const filter = new EcwidOrderFilter();
    ecwidOrders.value = await getEcwidOrders(
      dateRange.value.from,
      dateRange.value.to
    );

    const brewmanOrders = await orderService.getOrders(
      distinctGuids(ecwidOrders.value.map((o) => o.brewmanOrderId))
    );

    brewmanOrderMap.value = toGuidMap(
      brewmanOrders,
      (o) => o.header.readOnly.id
    );
  });

  onMounted(refresh);

  return {
    settings,
    meta,
    isActivated,
    beginAuth,
    completeAuth,
    disconnect,
    saveSettings,
    initiateOrderImport,
    dateRange,
    refreshOrders,
    refresh,
    ecwidOrders,
    brewmanOrderMap,
  };
}

export async function getEcwidOrders(
  fromDate?: LocalDate | null,
  toDate?: LocalDate | null
) {
  return (
    await ecwidOrderService.getEcwidOrdersByFilter(
      fillFilterDates(new EcwidOrderFilter(), fromDate, toDate)
    )
  ).ecwidOrders;
}
